import { z } from "zod";

export const zodJsonStringAsDate = z.preprocess(
  (d) => (typeof d === "string" ? new Date(d) : d),
  z.date(),
);

/**
 * Schema helper for optional query string array parameters.
 * Handles single values (?param=value), repeats (?param=a&param=b) and comma
 * separated lists (?param=a,b). Filters out empty strings and defaults to
 * empty array when not provided. Values themselves cannot contain a comma.
 *
 * Usage: zodQueryStringArray(z.string())
 */
export const zodQueryStringArray = <T extends z.ZodType>(schema: T) => {
  const splitValue = (value: string) =>
    value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);

  return z.preprocess((value) => {
    if (typeof value === "string") return splitValue(value);
    if (Array.isArray(value))
      return value.flatMap((item) =>
        typeof item === "string" ? splitValue(item) : item,
      );
    return value;
  }, z.array(schema).optional().default([]));
};

/**
 * Schema helper for optional query string boolean parameters (?param=true|false),
 * since query strings arrive as literal "true"/"false" strings, not booleans.
 */
export const zodQueryStringBoolean = () =>
  z
    .enum(["true", "false"])
    .transform((value) => value === "true")
    .optional();
